import { effortMinutes } from './activity.js'
import { addDays, startOfDay, startOfWeek } from './date.js'
import type { Activity } from './types.js'

export interface LifetimeStats {
  totalSessions: number
  totalMinutes: number
  totalEffortMinutes: number
  totalKcal: number
  /** Nombre de familles d'activité différentes pratiquées */
  varietyCount: number
  firstActivityAt: string | null
}

export function lifetimeStats(activities: readonly Activity[]): LifetimeStats {
  let totalMinutes = 0
  let totalEffortMinutes = 0
  let totalKcal = 0
  let first: string | null = null

  for (const activity of activities) {
    totalMinutes += activity.durationMin
    totalEffortMinutes += effortMinutes(activity)
    totalKcal += activity.kcal ?? 0
    if (first === null || activity.startedAt < first) first = activity.startedAt
  }

  return {
    totalSessions: activities.length,
    totalMinutes,
    totalEffortMinutes: Math.round(totalEffortMinutes),
    totalKcal,
    varietyCount: new Set(activities.map((a) => a.type)).size,
    firstActivityAt: first,
  }
}

/**
 * Les sept jours de la semaine en cours : a-t-on bougé, oui ou non ?
 *
 * Volontairement binaire et sans durée. C'est la représentation la moins
 * « comptable » de la régularité : on voit d'un coup d'œil sa semaine sans
 * lire un seul chiffre.
 */
export function activeDaysOfWeek(
  activities: readonly Activity[],
  now: Date = new Date(),
): boolean[] {
  const start = startOfWeek(now)
  const active = new Set(
    activities.map((activity) => startOfDay(new Date(activity.startedAt)).getTime()),
  )
  return Array.from({ length: 7 }, (_, offset) =>
    active.has(addDays(start, offset).getTime()),
  )
}

/**
 * Niveau d'énergie du compagnon, de 0 à 4.
 *
 * Dérivé de la vitalité, mais SANS état négatif : le palier le plus bas est
 * « endormi », jamais « épuisé ». Un compagnon qui dépérit culpabilise, et
 * culpabiliser quelqu'un qui ne bouge pas est le meilleur moyen de le perdre.
 */
export type EnergyLevel = 0 | 1 | 2 | 3 | 4

export function energyLevel(vitality: number): EnergyLevel {
  if (vitality < 12) return 0
  if (vitality < 32) return 1
  if (vitality < 55) return 2
  if (vitality < 80) return 3
  return 4
}
